import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/store';
import { createCollabClient, type CollabClient, type CollabStatus } from './yjs-client';

/**
 * React hook that lifts a (engagement_id, register_type) collab room into the
 * component tree. Returns the live Y.Doc + connection status + the awareness
 * handle for the presence/cursors component to bind to. The hook owns the
 * lifecycle — when the component unmounts (or the engagement/register pair
 * changes) the previous client is closed.
 *
 * When no auth token is present (logged-out / dev-auth shortcut not enabled),
 * status starts in 'degraded' so the UI can render the banner + fall back to
 * the REST PATCH path.
 */
export function useCollab(
  engagementId: string | undefined,
  registerType: string,
): {
  client: CollabClient | null;
  status: CollabStatus;
} {
  const token = useAuth((s) => s.token);
  const [status, setStatus] = useState<CollabStatus>('connecting');
  const clientRef = useRef<CollabClient | null>(null);

  // (Re)create the client when the engagement or register_type changes; the
  // memo captures the input pair so identity-stable args don't churn the
  // client.
  const key = useMemo(
    () => (engagementId ? `${engagementId}::${registerType}` : null),
    [engagementId, registerType],
  );

  useEffect(() => {
    if (!key || !engagementId) {
      setStatus('degraded');
      return undefined;
    }
    const client = createCollabClient({ engagementId, registerType, token });
    if (!client) {
      setStatus('degraded');
      return undefined;
    }
    clientRef.current = client;
    const unsubscribe = client.onStatus(setStatus);
    return () => {
      unsubscribe();
      client.close();
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token]);

  return { client: clientRef.current, status };
}
